import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { folderService } from "../../../services/folderService";
import { FiTrash2, FiEdit } from "react-icons/fi";
import { COLORS } from "@/utils/colors";
import ArrowDown from "@/utils/svgIcons";
import { useAuthStore } from "@/store/authStore";

interface Folder {
  id: string;
  name: string;
  description: string;
  createdAt?: string;
}

interface SidebarProps {
  onOpenAddFolder: () => void;
  folders?: Folder[];
  onFolderDeleted?: (id: string) => void;
  onFolderUpdated?: (id: string, updatedFolder: Folder) => void;
}

export default function Sidebar({
  onOpenAddFolder,
  folders = [],
  onFolderDeleted,
  onFolderUpdated,
}: SidebarProps) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedItem, setSelectedItem] = useState<string | null>(() => {
    // Initialize from localStorage
    return localStorage.getItem('selectedFolderId') || null;
  });
  const [deletingFolderId, setDeletingFolderId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Sync selectedItem with localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const storedId = localStorage.getItem('selectedFolderId');
      if (storedId !== selectedItem) {
        setSelectedItem(storedId);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [selectedItem]);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [editFormData, setEditFormData] = useState({ name: "", description: "" });
  const [updatingFolderId, setUpdatingFolderId] = useState<string | null>(null);


  // Search Functionality modal state

  // Search Functionality modal state


  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };



  const handleClick = (itemId: string) => {
    setSelectedItem(itemId);

    if (itemId === "view-all") {
      localStorage.setItem('selectedFolderId', 'view-all');
    } else if (itemId === "uncategorized") {
      localStorage.setItem('selectedFolderId', 'uncategorized');
    } else {
      localStorage.setItem('selectedFolderId', itemId);
    }

    window.dispatchEvent(new Event('folderChanged'));
    navigate('/dashboard/enclosure-builder/designs');
  };

  const sortedFolders = useMemo(() => {
    if (!Array.isArray(folders)) return [];

    // Extract timestamp from MongoDB ObjectId (first 8 hex chars = Unix seconds)
    const getTimestamp = (id: string) => parseInt(id.substring(0, 8), 16);

    return folders
      .filter(folder => folder && typeof folder.name === 'string')
      .sort((a, b) =>
        sortOrder === "desc"
          ? getTimestamp(b.id) - getTimestamp(a.id)   // newest first
          : getTimestamp(a.id) - getTimestamp(b.id)   // oldest first
      );
  }, [folders, sortOrder]);

  const handleToggleSort = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const openDeleteModal = (id: string) => {
    setPendingDeleteId(id);
    setShowDeleteModal(true);
  };

  const openEditModal = (folder: Folder) => {
    setEditingFolder(folder);
    setEditFormData({ name: folder.name, description: folder.description });
    setShowEditModal(true);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      setDeletingFolderId(pendingDeleteId);
      await folderService.deleteFolder(pendingDeleteId);

      // Notify parent or update state
      onFolderDeleted?.(pendingDeleteId);

      // After successful deletion, go to view-all
      localStorage.setItem('selectedFolderId', 'view-all');
      window.dispatchEvent(new Event('folderChanged'));
      navigate('/dashboard/enclosure-builder/designs');
    } catch (error) {
      console.error("Failed to delete folder:", error);
      alert("Failed to delete folder. Please try again.");
    } finally {
      setDeletingFolderId(null);
      setPendingDeleteId(null);
      setShowDeleteModal(false);
      handleClick("view-all");
    }
  };

  const confirmEdit = async () => {
    if (!editingFolder) return;
    try {
      setUpdatingFolderId(editingFolder.id);
      const updatedFolder = await folderService.updateFolder(editingFolder.id, editFormData);
      if (updatedFolder) {
        onFolderUpdated?.(editingFolder.id, updatedFolder);
      }
      setShowEditModal(false);
      setEditingFolder(null);
      setEditFormData({ name: "", description: "" });
    } catch (error) {
      console.error("Failed to update folder:", error);
      alert("Failed to update folder. Please try again.");
    } finally {
      setUpdatingFolderId(null);
    }
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      {/* Delete Modal with Light Overlay */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-md">
          <div
            style={{ backgroundColor: COLORS.white, borderColor: COLORS.primary }}

            className=" rounded-lg shadow-lg p-6 w-[90%] max-w-md border  ">
            <h2
              style={{ color: COLORS.black }}

              className="text-lg font-semibold text-gray-800 mb-4">Delete Folder</h2>
            <p
              style={{ color: COLORS.darkGray }}

              className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this folder? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{ backgroundColor: COLORS.primary, color: COLORS.white }}

                className="px-4 py-2   rounded">
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingFolderId === pendingDeleteId}
                style={{ backgroundColor: COLORS.orange, color: COLORS.white }}

                className="px-4 py-2   rounded   disabled:opacity-50">
                {deletingFolderId === pendingDeleteId ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal with Light Overlay */}
      {showEditModal && editingFolder && (
        <div className="fixed inset-0 bg-opacity-70 backdrop-blur-md flex items-center justify-center z-[9999]">
          <div
            style={{ backgroundColor: COLORS.white, borderColor: COLORS.primary }}

            className=" border rounded-lg shadow-lg p-6 w-[90%] max-w-md ">
            <h2
              style={{ color: COLORS.black }}
              className="text-lg font-semibold mb-4">Edit Folder</h2>
            <div className="space-y-4 mb-6">
              <div>
                <label
                  style={{ color: COLORS.darkGray }}
                  htmlFor="edit-folder-name" className="block text-sm font-medium mb-1">
                  Folder Name
                </label>
                <input
                  id="edit-folder-name"
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => handleEditFormChange("name", e.target.value)}
                  style={{ borderColor: COLORS.primary, backgroundColor: COLORS.white, color: COLORS.black }}
                  className="w-full px-3 py-2 border  rounded-md focus:outline-none  "
                  placeholder="Enter folder name"
                />
              </div>
              <div>
                <label htmlFor="edit-folder-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="edit-folder-description"
                  value={editFormData.description}
                  onChange={(e) => handleEditFormChange("description", e.target.value)}
                  // className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"

                  style={{ borderColor: COLORS.primary, backgroundColor: COLORS.white, color: COLORS.black }}

                  className="w-full px-3 py-2 border  rounded-md focus:outline-none  "

                  placeholder="Enter folder description"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingFolder(null);
                  setEditFormData({ name: "", description: "" });
                }}
                style={{ backgroundColor: COLORS.primary, color: COLORS.white }}
                className="px-4 py-2    rounded  "
              >
                Cancel
              </button>
              <button
                onClick={confirmEdit}
                disabled={updatingFolderId === editingFolder.id || !editFormData.name.trim()}
                style={{ backgroundColor: COLORS.orange, color: COLORS.white }}
                className="px-4 py-2    rounded   disabled:opacity-50"
              >
                {updatingFolderId === editingFolder.id ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div
        style={{ backgroundColor: COLORS.darkGray }}
        className="hidden fixed left-0 top-[55px] w-[270px] h-[calc(100vh-55px)] lg:flex flex-col"
      >
        {/* Top Section */}
        <div className="flex flex-col mt-[90px] px-5 gap-3 flex-1 overflow-hidden">

          {/* Search */}
          <form onSubmit={(e) => e.preventDefault()} className="mt-1">
            <div className="relative">
              <svg
                style={{ color: COLORS.folderSearchBorder }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
              </svg>
              <input
                style={{ backgroundColor: COLORS.folderSearch, borderColor: COLORS.folderSearchBorder, color: COLORS.white }}
                className="block w-full h-[38px] pl-9 pr-3 border rounded-lg outline-none text-sm placeholder-gray-500"
                type="text"
                placeholder="Search designs"
                value={searchTerm}
                onChange={handleSearchInputChange}
              />
            </div>
          </form>

          {/* Folders Section Header */}
          {user?.role === "user" && <div className="flex items-center justify-between mt-2">
            <span style={{ color: COLORS.white }} className="text-xl font-bold">
              Folders
            </span>
            <div className="flex items-center gap-3">
              {/* Sort toggle */}
              <button
                onClick={handleToggleSort}
                title={sortOrder === "desc" ? "Switch to oldest first" : "Switch to newest first"}
                style={{ color: COLORS.white }}
                className="opacity-60 hover:opacity-100 transition-opacity"
              >
                {sortOrder === "desc"
                  ? <ArrowDown className="w-4 h-4" />
                  : <ArrowDown className="w-4 h-4 transform rotate-180" style={{ stroke: 'white' }} />}
              </button>
              {/* Add Folder button — right of arrow */}
              <button
                onClick={onOpenAddFolder}
                title="New folder"
                style={{ borderColor: "rgba(255,255,255,0.2)", color: COLORS.white }}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium hover:bg-white/10 transition-all duration-150"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                New Folder
              </button>
            </div>
          </div>}
          {/* Folder List */}
          {user?.role === "user" && <div className="flex flex-col flex-1 overflow-hidden">

            {/* Uncategorized — always pinned at top, never scrolls */}
            <div
              onClick={() => handleClick("uncategorized")}
              style={{
                backgroundColor: selectedItem === "uncategorized" ? "rgba(255,255,255,0.08)" : "transparent",
                color: selectedItem === "uncategorized" ? COLORS.primary : COLORS.white,
              }}
              className="cursor-pointer px-2 py-1.5 transition-all duration-150 hover:opacity-80 flex-shrink-0"
            >
              <span className="text-sm">Uncategorized</span>
            </div>

            {/* Divider */}
            <hr style={{ borderColor: "rgba(255,255,255,0.08)" }} className="my-1 border-0 border-t flex-shrink-0" />

            {/* Scrollable middle — only folders scroll */}
            <div className="overflow-y-auto scrollbar-hide h-[calc(40vh-200px)] flex flex-col gap-0.5 pr-1">
              {sortedFolders.length === 0 && (
                <p style={{ color: COLORS.white }} className="text-xs opacity-30 italic px-2 py-3">No folders yet.</p>
              )}
              {sortedFolders.map((folder) => (
                <div
                  key={folder.id}
                  onClick={() => handleClick(folder.id)}
                  style={{
                    color: selectedItem === folder.id ? COLORS.primary : COLORS.white,
                    backgroundColor: selectedItem === folder.id ? "rgba(255,255,255,0.05)" : "transparent",
                  }}
                  className="group flex items-center justify-between gap-2 cursor-pointer px-2 py-1.5 transition-all duration-150 hover:opacity-80"
                >
                  <span className="text-sm truncate max-w-[160px]">{folder.name}</span>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button
                      type="button"
                      title="Edit folder"
                      onClick={(e) => { e.stopPropagation(); openEditModal(folder); }}
                      style={{ color: COLORS.white }}
                      className="p-1 rounded hover:bg-white/10 transition-colors opacity-60 hover:opacity-100"
                    >
                      <FiEdit size={13} />
                    </button>
                    <button
                      type="button"
                      title="Delete folder"
                      onClick={(e) => { e.stopPropagation(); openDeleteModal(folder.id); }}
                      className="p-1 rounded hover:bg-red-500/20 transition-colors text-red-400 opacity-60 hover:opacity-100"
                    >
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Divider */}
            <hr style={{ borderColor: "rgba(255,255,255,0.08)" }} className="my-1 border-0 border-t flex-shrink-0" />

            {/* View All Drawings — always pinned at bottom, never scrolls */}
            <div
              onClick={() => handleClick("view-all")}
              style={{ color: selectedItem === "view-all" ? COLORS.primary : COLORS.white }}
              className="cursor-pointer px-2 py-1.5 transition-all duration-150 hover:opacity-80 flex-shrink-0"
            >
              <span className="text-sm">View All Drawings</span>
            </div>

          </div>}
        </div>

        {/* Bottom Links */}
        {user?.role === "user" && <div
          style={{ color: COLORS.white, borderTopColor: "rgba(255,255,255,0.08)" }}
          className="flex flex-col text-xs mb-8 mx-5 gap-y-4 pb-4 pt-5 border-t">
          <p
            onClick={() => navigate("/dashboard/quote-form")}
            className="cursor-pointer opacity-50 hover:opacity-100 transition-opacity tracking-wider uppercase font-medium"
          >
            Custom Quote
          </p>
          <span
            onClick={() => navigate("/services")}
            className="flex items-center gap-x-2 cursor-pointer opacity-50 hover:opacity-100 transition-opacity"
          >
            <p className="p-0 m-0 tracking-wider uppercase font-medium">Design Guidelines</p>
            <ArrowDown className="transform -rotate-90 w-4 h-4" />
          </span>
          <span
            onClick={() => navigate("/dashboard/enclosure-builder/designs")}
            className="flex items-center gap-x-2 cursor-pointer opacity-50 hover:opacity-100 transition-opacity"
          >
            <p className="p-0 m-0 tracking-wider uppercase font-medium">Templates</p>
            <ArrowDown className="transform -rotate-90 w-4 h-4" />
          </span>
        </div>}
      </div>
    </>
  );
}
